import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CssBaseline,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Fade,
  Divider
} from "@mui/material";
import OpusLogo from "../../style/img/opus-logo.webp";
import { Lock, Visibility, VisibilityOff, Email, KeyboardReturn } from '@mui/icons-material';
import { ThemeProvider } from "@mui/material";
import darkTheme from "../../style/theme";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState(new Array(6).fill(""));
  const [combinedCode, setCombinedCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const username = searchParams.get("username");

  const handleRequestReset = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
      } else {
        setError(data.message || "Failed to request password reset code");
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    const code = combinedCode || verificationCode.join("");
    if (!code || code.length !== 6) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/confirm-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          confirmationCode: code,
          newPassword: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Error confirming password reset:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCombinedCodeChange = (e) => {
    const newValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setCombinedCode(newValue);

    // Update individual digits
    const newVerificationCode = new Array(6).fill("");
    for (let i = 0; i < newValue.length; i++) {
      newVerificationCode[i] = newValue[i];
    }
    setVerificationCode(newVerificationCode);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

if (!username) {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2c3e50 100%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={10}
          sx={{
            maxWidth: '500px',
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(50, 54, 56, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: 4,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <img
              src={OpusLogo}
              alt="Logo"
              style={{
                width: '100%',
                maxWidth: '250px',
                marginBottom: '2rem',
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
              Reset Password
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
              Please enter your username to begin the password reset process
            </Typography>
          </Box>
          
          {error && (
            <Fade in={Boolean(error)}>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    alignItems: 'center'
                  }
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}
          
          <Box component="form" onSubmit={(e) => {
            e.preventDefault();
            const usernameInput = document.getElementById('username-input').value;
            if (usernameInput) {
              window.location.href = `/reset-password?username=${encodeURIComponent(usernameInput)}`;
            } else {
              setError("Please enter a username");
            }
          }}>
            <TextField
              id="username-input"
              label="Username"
              variant="outlined"
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(69, 255, 167, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#45FFA7',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px rgba(50, 54, 56, 0.9) inset !important',
                    WebkitTextFillColor: '#fff !important',
                    caretColor: '#fff !important',
                    borderRadius: 'inherit',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Button
                variant="outlined"
                href="/login"
                sx={{
                  flex: 1,
                  py: 1.5,
                  color: "#fff",
                  fontWeight: "bold",
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  border: "1px solid #fff",
                  '&:hover': {
                    backgroundColor: "white",
                    color: "#000",
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Back to Login
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                sx={{
                  flex: 1,
                  py: 1.5,
                  backgroundColor: "#45FFA7",
                  color: "#000",
                  fontWeight: "bold",
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: '0 4px 20px rgba(69, 255, 167, 0.25)',
                  '&:hover': {
                    backgroundColor: "#3cdfa0",
                    boxShadow: '0 6px 25px rgba(69, 255, 167, 0.35)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2c3e50 100%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={10}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '550px',
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(50, 54, 56, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: 4,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <img
              src={OpusLogo}
              alt="Logo"
              style={{
                width: '100%',
                maxWidth: '250px',
                marginBottom: '1.5rem',
              }}
            />

            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
              Reset Password
            </Typography>

            <Typography variant="h5" sx={{ mt:4, color: 'rgba(255,255,255,0.7)', mb: 1 }}>
              Hi, <span style={{ color: '#fff' }}>{username}</span>
            </Typography>

            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, textAlign: "center" }}>
              {step === 1
                ? "We're here to help you reset your password. Click the button below to receive a verification code."
                : "Enter the verification code sent to your email and set a new password."}
            </Typography>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
          </Box>

          {error && (
            <Fade in={Boolean(error)}>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    alignItems: 'center'
                  }
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {success ? (
            <Fade in={success}>
              <Box sx={{ textAlign: "center" }}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      alignItems: 'center'
                    }
                  }}
                >
                  Password reset successfully! You can now log in.
                </Alert>

                <Button
                  variant="contained"
                  href="/login"
                  startIcon={<KeyboardReturn />}
                  style={{ color: 'black' }}
                  sx={{
                    marginTop: 2,
                    padding: '12px 20px',
                    backgroundColor: "#45FFA7",
                    fontWeight: "bold",
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    boxShadow: '0 4px 20px rgba(69, 255, 167, 0.25)',
                    '&:hover': {
                      backgroundColor: "#3cdfa0",
                      boxShadow: '0 6px 25px rgba(69, 255, 167, 0.35)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Return to Login
                </Button>
              </Box>
            </Fade>
          ) : step === 1 ? (
            <Fade in={step === 1}>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  onClick={handleRequestReset}
                  variant="outlined"
                  fullWidth
                  size="large"
                  type="submit"
                  color="white"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    color: "#fff",
                    fontWeight: "bold",
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    border: "1px solid #fff",
                    '&:hover': {
                      backgroundColor: "white",
                      color: "#000",
                      boxShadow: '0 6px 25px rgba(69, 255, 167, 0.35)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? "Sending Code..." : "Request Verification Code"}
                </Button>
              </Box>
            </Fade>
          ) : (
            <Fade in={step === 2}>
              <Box>
                <Typography variant="h6" sx={{ color: "white", mb: 2, textAlign: "center" }}>
                  Enter Verification Code
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="Verification Code"
                    fullWidth
                    value={combinedCode}
                    onChange={handleCombinedCodeChange}
                    placeholder="Enter 6-digit code"
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "new-password",
                      form: {
                        autoComplete: "off",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontSize: "0.875rem", color: 'rgba(255,255,255,0.5)' }}>
                            #
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          borderRadius: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(69, 255, 167, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#45FFA7',
                        },
                      },
                    }}
                  />
                  {/* <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                    {verificationCode.map((digit, index) => (
                      <Box 
                        key={index}
                        sx={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          fontSize: '1.25rem',
                          color: digit ? 'white' : 'rgba(255,255,255,0.2)',
                          backgroundColor: 'rgba(255,255,255,0.03)'
                        }}
                      >
                        {digit || '•'}
                      </Box>
                    ))}
                  </Box> */}
                </Box>

                <Typography variant="h6" sx={{ color: "white", mb: 2, textAlign: "center" }}>
                  Create New Password
                </Typography>

                <TextField
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password",
                    form: {
                      autoComplete: "off",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'rgba(255,255,255,0.5)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <Visibility sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)',
                        borderRadius: 2,
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(69, 255, 167, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#45FFA7',
                      },
                    },
                  }}
                />

                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="off"
                  inputProps={{
                    autoComplete: "off",
                    form: {
                      autoComplete: "off",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'rgba(255,255,255,0.5)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <Visibility sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)',
                        borderRadius: 2,
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(69, 255, 167, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#45FFA7',
                      },
                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleConfirmReset}
                  fullWidth
                  color="white"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    color: "#fff",
                    fontWeight: "bold",
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    border: "1px solid #fff",
                    '&:hover': {
                      backgroundColor: "white",
                      color: "#000",
                      boxShadow: '0 6px 25px rgba(69, 255, 167, 0.35)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? "Resetting Password..." : "Reset Password"}
                </Button>
              </Box>
            </Fade>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default ResetPassword;